/**
 * Phase 341 — Fire Extinguisher Certifications tab for a building.
 *
 * Mirrors the visual structure of the Elevator Certification tab — a "Latest
 * Certification" highlight card at the top with a sortable history table
 * below — but with a simpler schema (no pass/fail, no failure-reason chips).
 *
 * Form fields (all editable):
 *   • Company Name (autocomplete from /api/fire-extinguisher-companies)
 *   • Quantity (int)
 *   • Unit Price (€)
 *   • Total Amount (€, auto-computed but overridable)
 *   • Contract Number
 *   • Contract Start Date
 *   • Contract Expiry Date
 *   • Certification Date (drives "latest" sort + {{fire_*}} placeholder)
 *   • Notes (multi-line, resizable)
 *
 * Generate Email parity: this tab does not add its own button — the global
 * "Generate Email" button in the BuildingDetails header still opens the
 * Email Templates panel where the placeholder picker now lists the {{fire_*}}
 * tokens.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import {
  FireSimple, PlusCircle, PencilSimple, Trash, FloppyDisk,
} from '@phosphor-icons/react';
import { useLanguage } from '../context/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const formatDateDMY = (s) => {
  if (!s || s.length < 10) return s || '—';
  const [y, m, d] = s.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
};
const fmtEur = (n) => {
  const v = Number(n || 0);
  return `${v < 0 ? '-' : ''}€${Math.abs(v).toFixed(2)}`;
};

const emptyForm = {
  company_name: '',
  company_id: '',
  quantity: 0,
  unit_price: 0,
  total_amount: 0,
  contract_number: '',
  contract_start_date: '',
  contract_end_date: '',
  certification_date: new Date().toISOString().slice(0, 10),
  certification_expiry_date: '',
  notes: '',
};

export default function FireCertificationTab({ buildingId, isAdmin = true }) {
  const { t } = useLanguage();

  const [certs, setCerts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(''); // '' = create mode
  const [form, setForm] = useState({ ...emptyForm });

  // Phase 343 — Sub-tabs inside Fire Certification page.
  // 'certifications' (current default), 'invoices', 'payments', 'statement'.
  const [subTab, setSubTab] = useState('certifications');
  const [statement, setStatement] = useState(null);
  const [statementLoading, setStatementLoading] = useState(false);

  const fetchStatement = useCallback(async (certId) => {
    if (!certId) { setStatement(null); return; }
    setStatementLoading(true);
    try {
      const res = await axios.get(`${API}/buildings/${buildingId}/fire-certifications/${certId}/statement`);
      setStatement(res.data || null);
    } catch {
      setStatement(null);
    } finally {
      setStatementLoading(false);
    }
  }, [buildingId]);

  // Phase 344 — Inline Edit/Delete for invoices & payments.
  // Invoice edit operates on the `expenses` collection via /api/expenses.
  // Payment edit operates on `expense_payments` via /api/expense-payments.
  // Phase 356 — `window.confirm` is unreliable inside the Capacitor wrapper
  // and silently dismissed by some browsers in iframes/preview mode, which
  // made the Delete buttons appear "broken". Replaced with a Shadcn Dialog
  // confirmation that we fully control.
  const [confirmDel, setConfirmDel] = useState(null); // {kind:'invoice'|'payment', item, busy:false}
  const askDeleteInvoice = (inv) => setConfirmDel({ kind: 'invoice', item: inv, busy: false });
  const askDeletePayment = (p) => setConfirmDel({ kind: 'payment', item: p, busy: false });
  const closeConfirmDel = () => setConfirmDel(null);
  const performConfirmedDelete = async () => {
    if (!confirmDel || confirmDel.busy) return;
    setConfirmDel(prev => ({ ...prev, busy: true }));
    try {
      if (confirmDel.kind === 'invoice') {
        await axios.delete(`${API}/expenses/${confirmDel.item.expense_id}`);
      } else {
        await axios.delete(`${API}/expense-payments/${confirmDel.item.payment_id}`);
      }
      toast.success(t('deletedSuccess') || 'Deleted');
      setConfirmDel(null);
      const certId = certs[0]?.cert_id;
      if (certId) await fetchStatement(certId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('errorOccurred') || 'Error');
      setConfirmDel(prev => prev ? { ...prev, busy: false } : null);
    }
  };

  const [invEditOpen, setInvEditOpen] = useState(false);
  const [invEditForm, setInvEditForm] = useState({ expense_id: '', date: '', amount: 0, notes: '', reference_number: '' });
  const openInvEdit = (inv) => {
    setInvEditForm({
      expense_id: inv.expense_id || '',
      date: (inv.date || '').slice(0, 10),
      amount: Number(inv.amount || 0),
      notes: inv.notes || '',
      reference_number: inv.reference_number || '',
    });
    setInvEditOpen(true);
  };
  const saveInvEdit = async () => {
    try {
      await axios.put(`${API}/expenses/${invEditForm.expense_id}`, {
        date: invEditForm.date,
        amount: Number(invEditForm.amount),
        notes: invEditForm.notes,
        reference_number: invEditForm.reference_number,
      });
      toast.success(t('savedSuccess') || 'Saved');
      setInvEditOpen(false);
      const certId = certs[0]?.cert_id;
      if (certId) fetchStatement(certId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('errorOccurred'));
    }
  };
  const deleteInvoice = (inv) => askDeleteInvoice(inv);

  const [payEditOpen, setPayEditOpen] = useState(false);
  const [payEditForm, setPayEditForm] = useState({ payment_id: '', date: '', amount: 0, notes: '', payment_source: 'cash', payment_number: '' });
  const openPayEdit = (p) => {
    setPayEditForm({
      payment_id: p.payment_id || '',
      date: (p.date || '').slice(0, 10),
      amount: Number(p.amount || 0),
      notes: p.notes || '',
      payment_source: p.payment_source || p.source || 'cash',
      payment_number: p.payment_number || p.receipt_number || '',
    });
    setPayEditOpen(true);
  };
  const savePayEdit = async () => {
    try {
      await axios.put(`${API}/expense-payments/${payEditForm.payment_id}`, {
        date: payEditForm.date,
        amount: Number(payEditForm.amount),
        notes: payEditForm.notes,
        payment_source: payEditForm.payment_source,
        payment_number: payEditForm.payment_number,
      });
      toast.success(t('savedSuccess') || 'Saved');
      setPayEditOpen(false);
      const certId = certs[0]?.cert_id;
      if (certId) fetchStatement(certId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('errorOccurred'));
    }
  };
  const deletePayment = (p) => askDeletePayment(p);

  const fetchCerts = useCallback(async () => {
    if (!buildingId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/buildings/${buildingId}/fire-certifications`);
      setCerts(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error(t('errorOccurred') || 'Error');
    } finally {
      setLoading(false);
    }
  }, [buildingId, t]);

  const fetchCompanies = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/fire-extinguisher-companies`);
      setCompanies(Array.isArray(res.data) ? res.data : []);
    } catch {
      setCompanies([]);
    }
  }, []);

  useEffect(() => { fetchCerts(); fetchCompanies(); }, [fetchCerts, fetchCompanies]);

  // Phase 343 — Auto-load statement whenever the user picks one of the data
  // sub-tabs (Invoices/Payments/Statement). Driven off the latest cert.
  useEffect(() => {
    if (subTab !== 'certifications' && certs.length > 0) {
      fetchStatement(certs[0].cert_id);
    }
  }, [subTab, certs, fetchStatement]);

  // Auto-compute total whenever quantity / unit_price change unless the user
  // has explicitly overridden the total (we treat "override" as any manual
  // edit — see handleTotalChange below).
  const [totalManuallyEdited, setTotalManuallyEdited] = useState(false);
  useEffect(() => {
    if (!totalManuallyEdited) {
      const t2 = Number(form.quantity || 0) * Number(form.unit_price || 0);
      setForm(prev => ({ ...prev, total_amount: Math.round(t2 * 100) / 100 }));
    }
  }, [form.quantity, form.unit_price, totalManuallyEdited]);

  const latest = useMemo(() => certs[0] || null, [certs]);
  const history = useMemo(() => certs.slice(1), [certs]);

  const openCreate = () => {
    setEditingId('');
    setForm({ ...emptyForm });
    setTotalManuallyEdited(false);
    setDialogOpen(true);
  };

  const openEdit = (cert) => {
    setEditingId(cert.cert_id);
    setForm({
      company_name: cert.company_name || '',
      company_id: cert.company_id || '',
      quantity: cert.quantity || 0,
      unit_price: cert.unit_price || 0,
      total_amount: cert.total_amount || 0,
      contract_number: cert.contract_number || '',
      contract_start_date: cert.contract_start_date || '',
      contract_end_date: cert.contract_end_date || '',
      certification_date: cert.certification_date || '',
      certification_expiry_date: cert.certification_expiry_date || '',
      notes: cert.notes || '',
    });
    setTotalManuallyEdited(true); // Don't clobber stored total on first render.
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.company_id) {
      toast.error(t('companyRequired') || 'Please select a company');
      return;
    }
    try {
      if (editingId) {
        await axios.put(`${API}/buildings/${buildingId}/fire-certifications/${editingId}`, form);
        toast.success(t('savedSuccess') || 'Saved');
      } else {
        await axios.post(`${API}/buildings/${buildingId}/fire-certifications`, form);
        toast.success(t('savedSuccess') || 'Saved');
      }
      setDialogOpen(false);
      fetchCerts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('errorOccurred') || 'Save failed');
    }
  };

  // Phase 356 — Confirm-delete dialog for the certification record itself.
  // Same UX as the invoice/payment deletion above.
  const [certConfirmDel, setCertConfirmDel] = useState(null);
  const handleDelete = (cert) => setCertConfirmDel({ cert, busy: false });
  const performCertDelete = async () => {
    if (!certConfirmDel || certConfirmDel.busy) return;
    setCertConfirmDel(prev => ({ ...prev, busy: true }));
    try {
      await axios.delete(`${API}/buildings/${buildingId}/fire-certifications/${certConfirmDel.cert.cert_id}`);
      toast.success(t('deletedSuccess') || 'Deleted');
      setCertConfirmDel(null);
      fetchCerts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('errorOccurred') || 'Error');
      setCertConfirmDel(prev => prev ? { ...prev, busy: false } : null);
    }
  };

  return (
    <div className="space-y-4" data-testid="fire-certification-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FireSimple size={18} weight="fill" className="text-orange-600" />
          <h2 className="text-base font-semibold tracking-wide" style={{ fontFamily: 'var(--font-heading)' }}>
            {t('fireCertification') || 'Fire Extinguisher Certification'}
          </h2>
        </div>
        {isAdmin && (
          <Button onClick={openCreate} className="bg-zinc-900 hover:bg-zinc-800 rounded-none" size="sm" data-testid="fire-cert-add-btn">
            <PlusCircle size={14} className="mr-1" />
            {t('addCertification') || 'Add Certification'}
          </Button>
        )}
      </div>

      {/* Phase 343 — Sub-tabs: Certifications / Invoices / Payments / Statement.
          Same UX pattern as the Email Templates Compose/History toggle. */}
      <div className="flex border-b border-zinc-200" data-testid="fire-sub-tabs">
        {['certifications', 'invoices', 'payments', 'statement'].map(tk => (
          <button
            key={tk}
            type="button"
            onClick={() => setSubTab(tk)}
            className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${subTab === tk ? 'border-b-2 border-orange-600 text-zinc-900' : 'text-zinc-500 hover:text-zinc-700'}`}
            data-testid={`fire-sub-tab-${tk}`}
          >
            {t(tk === 'certifications' ? 'certifications' : tk) || (tk[0].toUpperCase() + tk.slice(1))}
          </button>
        ))}
      </div>

      {subTab === 'certifications' && (<>

      {/* Latest certification highlight card */}
      <div className="border border-zinc-200 bg-white">
        <div className="px-3 py-2 border-b border-zinc-200 bg-orange-50 flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wider text-orange-800 flex items-center gap-1">
            <FireSimple size={12} weight="fill" /> {t('latestCertification') || 'Latest Certification'}
          </span>
          {latest && isAdmin && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => openEdit(latest)}
                className="text-xs px-1.5 py-0.5 border border-zinc-200 text-zinc-500 hover:bg-white"
                title={t('edit') || 'Edit'}
                data-testid="fire-cert-edit-latest"
              >
                <PencilSimple size={12} />
              </button>
              <button
                onClick={() => handleDelete(latest)}
                className="text-xs px-1.5 py-0.5 border border-zinc-200 text-zinc-500 hover:bg-white hover:text-red-600"
                title={t('delete') || 'Delete'}
                data-testid="fire-cert-delete-latest"
              >
                <Trash size={12} />
              </button>
            </div>
          )}
        </div>
        <div className="p-3">
          {loading && <p className="text-xs text-zinc-500">{t('loading') || 'Loading…'}</p>}
          {!loading && !latest && (
            <p className="text-xs text-zinc-500 italic text-center py-6">
              {t('noFireCertYet') || 'No fire extinguisher certification recorded yet. Click "Add Certification" to create the first one.'}
            </p>
          )}
          {!loading && latest && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div>
                <p className="text-[9px] text-zinc-400 uppercase tracking-wider">{t('companyName') || 'Company Name'}</p>
                <p className="text-sm font-semibold text-zinc-800" data-testid="fire-latest-company">{latest.company_name || '—'}</p>
              </div>
              <div>
                <p className="text-[9px] text-zinc-400 uppercase tracking-wider">{t('certificationDate') || 'Certification Date'}</p>
                <p className="text-zinc-700" data-testid="fire-latest-cert-date">{formatDateDMY(latest.certification_date)}</p>
              </div>
              <div>
                <p className="text-[9px] text-zinc-400 uppercase tracking-wider">{t('certificationExpiryDate') || 'Certification Expiry'}</p>
                <p className="text-zinc-700" data-testid="fire-latest-cert-expiry">{formatDateDMY(latest.certification_expiry_date)}</p>
              </div>
              <div>
                <p className="text-[9px] text-zinc-400 uppercase tracking-wider">{t('quantity') || 'Quantity'}</p>
                <p className="text-zinc-700">{latest.quantity || 0}</p>
              </div>
              <div>
                <p className="text-[9px] text-zinc-400 uppercase tracking-wider">{t('unitPrice') || 'Unit Price'}</p>
                <p className="text-zinc-700">{fmtEur(latest.unit_price)}</p>
              </div>
              <div>
                <p className="text-[9px] text-zinc-400 uppercase tracking-wider">{t('totalAmount') || 'Total'}</p>
                <p className="text-sm font-semibold text-zinc-800" data-testid="fire-latest-total">{fmtEur(latest.total_amount)}</p>
              </div>
              <div>
                <p className="text-[9px] text-zinc-400 uppercase tracking-wider">{t('contractNumber') || 'Contract #'}</p>
                <p className="text-zinc-700 font-mono">{latest.contract_number || '—'}</p>
              </div>
              <div>
                <p className="text-[9px] text-zinc-400 uppercase tracking-wider">{t('contractStartDate') || 'Start'}</p>
                <p className="text-zinc-700">{formatDateDMY(latest.contract_start_date)}</p>
              </div>
              <div>
                <p className="text-[9px] text-zinc-400 uppercase tracking-wider">{t('contractEndDate') || 'End'}</p>
                <p className="text-zinc-700">{formatDateDMY(latest.contract_end_date)}</p>
              </div>
              {latest.notes && (
                <div className="md:col-span-4">
                  <p className="text-[9px] text-zinc-400 uppercase tracking-wider">{t('notes') || 'Notes'}</p>
                  <pre
                    className="bg-zinc-50 border border-zinc-200 mt-1 px-2.5 py-2 text-[11px] text-zinc-700 whitespace-pre-wrap break-words font-sans max-h-[280px] overflow-y-auto"
                    data-testid="fire-latest-notes"
                  >{latest.notes}</pre>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* History */}
      {history.length > 0 && (
        <div className="border border-zinc-200 bg-white" data-testid="fire-cert-history">
          <div className="px-3 py-2 border-b border-zinc-200 bg-zinc-50">
            <span className="text-xs font-semibold uppercase tracking-wider text-zinc-600">{t('history') || 'History'}</span>
            <span className="ml-2 text-[10px] text-zinc-400">{history.length}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-zinc-600">{t('certificationDate') || 'Date'}</th>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-zinc-600">{t('companyName') || 'Company'}</th>
                  <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wider text-zinc-600">{t('quantity') || 'Qty'}</th>
                  <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wider text-zinc-600">{t('totalAmount') || 'Total'}</th>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-zinc-600">{t('contractNumber') || 'Contract'}</th>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-zinc-600">{t('contractEndDate') || 'Expiry'}</th>
                  <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wider text-zinc-600">{t('actions') || ''}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {history.map(cert => (
                  <tr key={cert.cert_id} className="hover:bg-zinc-50" data-testid={`fire-history-row-${cert.cert_id}`}>
                    <td className="px-3 py-2 font-mono text-zinc-700">{formatDateDMY(cert.certification_date)}</td>
                    <td className="px-3 py-2 text-zinc-800 font-medium">{cert.company_name || '—'}</td>
                    <td className="px-3 py-2 text-right text-zinc-700">{cert.quantity || 0}</td>
                    <td className="px-3 py-2 text-right font-mono text-zinc-800">{fmtEur(cert.total_amount)}</td>
                    <td className="px-3 py-2 font-mono text-zinc-700">{cert.contract_number || '—'}</td>
                    <td className="px-3 py-2 text-zinc-700">{formatDateDMY(cert.contract_end_date)}</td>
                    <td className="px-3 py-2 text-right">
                      {isAdmin && (
                        <div className="inline-flex items-center gap-1">
                          <button onClick={() => openEdit(cert)} className="text-xs px-1.5 py-0.5 border border-zinc-200 text-zinc-500 hover:bg-zinc-50" title={t('edit') || 'Edit'} data-testid={`fire-history-edit-${cert.cert_id}`}>
                            <PencilSimple size={12} />
                          </button>
                          <button onClick={() => handleDelete(cert)} className="text-xs px-1.5 py-0.5 border border-zinc-200 text-zinc-500 hover:bg-zinc-50 hover:text-red-600" title={t('delete') || 'Delete'} data-testid={`fire-history-delete-${cert.cert_id}`}>
                            <Trash size={12} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      </>)}

      {/* Phase 343 — INVOICES tab. Shows the expenses linked to the latest
          Fire Cert. The "+ Add invoice" CTA scrolls the user to the global
          Expenses page where they can create the expense with this cert
          already pre-selected (via query string). */}
      {subTab === 'invoices' && (
        <div className="border border-zinc-200 bg-white" data-testid="fire-invoices-panel">
          <div className="px-3 py-2 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-zinc-600">{t('invoices') || 'Invoices'}</span>
            {certs[0] && (
              <a
                href={`/buildings/${buildingId}?tab=expenses&fire_certification_id=${certs[0].cert_id}`}
                className="text-[10px] underline text-zinc-500 hover:text-zinc-900"
                data-testid="fire-add-invoice-link"
              >
                + {t('addInvoice') || 'Add invoice'}
              </a>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="fire-invoices-table">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-zinc-600">{t('date') || 'Date'}</th>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-zinc-600">{t('description') || 'Description'}</th>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-zinc-600">{t('reference') || 'Reference'}</th>
                  <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wider text-zinc-600">{t('amount') || 'Amount'}</th>
                  <th className="px-3 py-2 text-center text-[10px] font-semibold uppercase tracking-wider text-zinc-600">{t('status') || 'Status'}</th>
                  {isAdmin && <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wider text-zinc-600 w-20">{t('actions') || 'Actions'}</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {statementLoading && (
                  <tr><td colSpan={isAdmin ? 6 : 5} className="px-3 py-6 text-xs text-zinc-500 text-center">{t('loading') || 'Loading…'}</td></tr>
                )}
                {!statementLoading && (!statement?.invoices || statement.invoices.length === 0) && (
                  <tr><td colSpan={isAdmin ? 6 : 5} className="px-3 py-8 text-xs text-zinc-500 text-center italic">{t('noFireInvoices') || 'No invoices linked yet.'}</td></tr>
                )}
                {!statementLoading && statement?.invoices?.map(inv => (
                  <tr key={inv.expense_id} className="hover:bg-zinc-50" data-testid={`fire-invoice-row-${inv.expense_id}`}>
                    <td className="px-3 py-2 font-mono text-zinc-700">{formatDateDMY(inv.date)}</td>
                    <td className="px-3 py-2 text-zinc-800">{inv.notes || '—'}</td>
                    <td className="px-3 py-2 font-mono text-zinc-700">{inv.reference_number || '—'}</td>
                    <td className="px-3 py-2 text-right font-mono text-zinc-800">{fmtEur(inv.amount)}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`text-[10px] px-1.5 py-0.5 border font-semibold uppercase ${inv.status === 'paid' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                        {inv.status || '—'}
                      </span>
                    </td>
                    {isAdmin && (
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button onClick={() => openInvEdit(inv)} className="text-xs px-1.5 py-0.5 border border-zinc-200 text-zinc-500 hover:bg-zinc-50" title={t('edit') || 'Edit'} data-testid={`fire-invoice-edit-${inv.expense_id}`}>
                            <PencilSimple size={12} />
                          </button>
                          <button onClick={() => deleteInvoice(inv)} className="text-xs px-1.5 py-0.5 border border-zinc-200 text-zinc-500 hover:bg-zinc-50 hover:text-red-600" title={t('delete') || 'Delete'} data-testid={`fire-invoice-delete-${inv.expense_id}`}>
                            <Trash size={12} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              {statement?.invoices?.length > 0 && (
                <tfoot className="bg-zinc-50 border-t border-zinc-200">
                  <tr>
                    <td colSpan={3} className="px-3 py-2 text-right text-[10px] uppercase tracking-wider font-semibold text-zinc-600">{t('total') || 'Total'}</td>
                    <td className="px-3 py-2 text-right font-mono font-bold text-zinc-900" data-testid="fire-invoices-total">{fmtEur(statement.invoices_total)}</td>
                    <td colSpan={isAdmin ? 2 : 1}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {/* Phase 343 — PAYMENTS tab. Reuses the existing expense_payments
          collection (auto-created by the expense's payment_source). */}
      {subTab === 'payments' && (
        <div className="border border-zinc-200 bg-white" data-testid="fire-payments-panel">
          <div className="px-3 py-2 border-b border-zinc-200 bg-zinc-50">
            <span className="text-xs font-semibold uppercase tracking-wider text-zinc-600">{t('payments') || 'Payments'}</span>
            <span className="ml-2 text-[10px] text-zinc-400">
              {t('paymentsAutoHint') || 'Payments auto-allocate from Cash/Bank Receipts when you set the source on the invoice.'}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="fire-payments-table">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-zinc-600">{t('date') || 'Date'}</th>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-zinc-600">{t('source') || 'Source'}</th>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-zinc-600">{t('description') || 'Description'}</th>
                  <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wider text-zinc-600">{t('amount') || 'Amount'}</th>
                  {isAdmin && <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wider text-zinc-600 w-20">{t('actions') || 'Actions'}</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {statementLoading && (
                  <tr><td colSpan={isAdmin ? 5 : 4} className="px-3 py-6 text-xs text-zinc-500 text-center">{t('loading') || 'Loading…'}</td></tr>
                )}
                {!statementLoading && (!statement?.payments || statement.payments.length === 0) && (
                  <tr><td colSpan={isAdmin ? 5 : 4} className="px-3 py-8 text-xs text-zinc-500 text-center italic">{t('noFirePayments') || 'No payments allocated yet.'}</td></tr>
                )}
                {!statementLoading && statement?.payments?.map(p => (
                  <tr key={p.payment_id} className="hover:bg-zinc-50" data-testid={`fire-payment-row-${p.payment_id}`}>
                    <td className="px-3 py-2 font-mono text-zinc-700">{formatDateDMY(p.date)}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] px-1.5 py-0.5 border font-semibold uppercase ${(p.payment_source || p.source) === 'bank' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
                        {p.payment_source || p.source || 'cash'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-zinc-700">{p.notes || '—'}</td>
                    <td className="px-3 py-2 text-right font-mono text-zinc-800">{fmtEur(p.amount)}</td>
                    {isAdmin && (
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button onClick={() => openPayEdit(p)} className="text-xs px-1.5 py-0.5 border border-zinc-200 text-zinc-500 hover:bg-zinc-50" title={t('edit') || 'Edit'} data-testid={`fire-payment-edit-${p.payment_id}`}>
                            <PencilSimple size={12} />
                          </button>
                          <button onClick={() => deletePayment(p)} className="text-xs px-1.5 py-0.5 border border-zinc-200 text-zinc-500 hover:bg-zinc-50 hover:text-red-600" title={t('delete') || 'Delete'} data-testid={`fire-payment-delete-${p.payment_id}`}>
                            <Trash size={12} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              {statement?.payments?.length > 0 && (
                <tfoot className="bg-zinc-50 border-t border-zinc-200">
                  <tr>
                    <td colSpan={3} className="px-3 py-2 text-right text-[10px] uppercase tracking-wider font-semibold text-zinc-600">{t('total') || 'Total'}</td>
                    <td className="px-3 py-2 text-right font-mono font-bold text-zinc-900" data-testid="fire-payments-total">{fmtEur(statement.payments_total)}</td>
                    {isAdmin && <td></td>}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {/* Phase 343/344 — STATEMENT tab. Unified chronological ledger
          (Date · Invoice# · Invoice Amt · Receipt# · Receipt Amt · Notes ·
          Running Balance) plus a totals summary. */}
      {subTab === 'statement' && (
        <div className="border border-zinc-200 bg-white" data-testid="fire-statement-panel">
          <div className="px-3 py-2 border-b border-zinc-200 bg-zinc-50">
            <span className="text-xs font-semibold uppercase tracking-wider text-zinc-600">{t('statement') || 'Statement'}</span>
          </div>
          {statementLoading && <p className="text-xs text-zinc-500 text-center py-6">{t('loading') || 'Loading…'}</p>}
          {!statementLoading && !statement && (
            <p className="text-xs text-zinc-500 italic text-center py-6">{t('noFireCertYet') || 'No fire extinguisher certification recorded yet.'}</p>
          )}
          {!statementLoading && statement && (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="fire-statement-ledger">
                  <thead className="bg-zinc-50 border-b border-zinc-200">
                    <tr>
                      <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-zinc-600">{t('date') || 'Date'}</th>
                      <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-zinc-600">{t('invoiceNumber') || 'Invoice #'}</th>
                      <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wider text-zinc-600">{t('invoiceAmount') || 'Invoice Amount'}</th>
                      <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-zinc-600">{t('receiptNumber') || 'Receipt #'}</th>
                      <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wider text-zinc-600">{t('receiptAmount') || 'Receipt Amount'}</th>
                      <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-zinc-600">{t('notes') || 'Notes'}</th>
                      <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wider text-zinc-600">{t('runningBalance') || 'Running Balance'}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {/* Opening balance row */}
                    <tr className="bg-zinc-50/60">
                      <td className="px-3 py-2 font-mono text-zinc-500">—</td>
                      <td colSpan={5} className="px-3 py-2 text-zinc-600 italic">{t('openingBalance') || 'Opening Balance'}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold text-zinc-800" data-testid="fire-ledger-opening">{fmtEur(statement.opening_balance)}</td>
                    </tr>
                    {(!statement.ledger || statement.ledger.length === 0) && (
                      <tr><td colSpan={7} className="px-3 py-8 text-xs text-zinc-500 text-center italic">{t('noLedgerEntries') || 'No entries yet.'}</td></tr>
                    )}
                    {(statement.ledger || []).map((row, idx) => (
                      <tr
                        key={`${row.type}-${row.expense_id || ''}-${row.payment_id || ''}-${idx}`}
                        className={`hover:bg-zinc-50 ${row.type === 'invoice' ? '' : 'bg-emerald-50/30'}`}
                        data-testid={`fire-ledger-row-${idx}`}
                      >
                        <td className="px-3 py-2 font-mono text-zinc-700 whitespace-nowrap">{formatDateDMY(row.date)}</td>
                        <td className="px-3 py-2 font-mono text-zinc-700">{row.invoice_number || (row.type === 'invoice' ? '—' : '')}</td>
                        <td className="px-3 py-2 text-right font-mono text-zinc-800">{row.type === 'invoice' ? fmtEur(row.invoice_amount) : ''}</td>
                        <td className="px-3 py-2 font-mono text-zinc-700">{row.receipt_number || (row.type === 'payment' ? '—' : '')}</td>
                        <td className="px-3 py-2 text-right font-mono text-emerald-700">{row.type === 'payment' ? `−${fmtEur(row.receipt_amount)}` : ''}</td>
                        <td className="px-3 py-2 text-zinc-700 max-w-[260px] truncate" title={row.notes || ''}>{row.notes || '—'}</td>
                        <td className={`px-3 py-2 text-right font-mono font-semibold ${row.running_balance > 0 ? 'text-amber-700' : 'text-zinc-800'}`}>{fmtEur(row.running_balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-zinc-100 border-t-2 border-zinc-300">
                    <tr>
                      <td colSpan={2} className="px-3 py-2 text-right text-[10px] uppercase tracking-wider font-semibold text-zinc-600">{t('totals') || 'Totals'}</td>
                      <td className="px-3 py-2 text-right font-mono font-bold text-zinc-900">{fmtEur(statement.invoices_total)}</td>
                      <td></td>
                      <td className="px-3 py-2 text-right font-mono font-bold text-emerald-700">−{fmtEur(statement.payments_total)}</td>
                      <td className="px-3 py-2 text-right text-[10px] uppercase tracking-wider font-semibold text-zinc-600">{t('remainingBalance') || 'Remaining'}</td>
                      <td className={`px-3 py-2 text-right font-mono font-bold ${statement.remaining > 0 ? 'text-amber-700' : 'text-emerald-700'}`} data-testid="fire-statement-remaining">{fmtEur(statement.remaining)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* Phase 344 — INVOICE EDIT dialog (reuses /api/expenses PUT) */}
      <Dialog open={invEditOpen} onOpenChange={setInvEditOpen}>
        <DialogContent className="sm:max-w-md" data-testid="fire-inv-edit-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{t('editInvoice') || 'Edit Invoice'}</DialogTitle>
            <DialogDescription>{t('editInvoiceHint') || 'Edits update the linked expense in real time.'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{t('date') || 'Date'}</Label>
                <Input type="date" value={invEditForm.date} onChange={(e) => setInvEditForm(prev => ({ ...prev, date: e.target.value }))} className="rounded-none mt-1" data-testid="fire-inv-edit-date" />
              </div>
              <div>
                <Label className="text-xs">{t('amount') || 'Amount'} (€)</Label>
                <Input type="number" step="0.01" min="0" value={invEditForm.amount} onChange={(e) => setInvEditForm(prev => ({ ...prev, amount: parseFloat(e.target.value || 0) }))} className="rounded-none mt-1" data-testid="fire-inv-edit-amount" />
              </div>
            </div>
            <div>
              <Label className="text-xs">{t('invoiceNumber') || 'Invoice Number'}</Label>
              <Input value={invEditForm.reference_number} onChange={(e) => setInvEditForm(prev => ({ ...prev, reference_number: e.target.value }))} className="rounded-none mt-1 font-mono" data-testid="fire-inv-edit-ref" />
            </div>
            <div>
              <Label className="text-xs">{t('notes') || 'Notes'}</Label>
              <Textarea value={invEditForm.notes} onChange={(e) => setInvEditForm(prev => ({ ...prev, notes: e.target.value }))} rows={3} className="rounded-none mt-1 resize-y" style={{ minHeight: '80px', maxHeight: '40vh' }} data-testid="fire-inv-edit-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInvEditOpen(false)} className="rounded-none">{t('cancel') || 'Cancel'}</Button>
            <Button onClick={saveInvEdit} className="rounded-none bg-zinc-900 hover:bg-zinc-800" data-testid="fire-inv-edit-save">
              <FloppyDisk size={14} className="mr-1" /> {t('save') || 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 344 — PAYMENT EDIT dialog (reuses /api/expense-payments PUT) */}
      <Dialog open={payEditOpen} onOpenChange={setPayEditOpen}>
        <DialogContent className="sm:max-w-md" data-testid="fire-pay-edit-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{t('editPayment') || 'Edit Payment'}</DialogTitle>
            <DialogDescription>{t('editPaymentHint') || 'Edits adjust Cash / Bank balances automatically.'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{t('date') || 'Date'}</Label>
                <Input type="date" value={payEditForm.date} onChange={(e) => setPayEditForm(prev => ({ ...prev, date: e.target.value }))} className="rounded-none mt-1" data-testid="fire-pay-edit-date" />
              </div>
              <div>
                <Label className="text-xs">{t('amount') || 'Amount'} (€)</Label>
                <Input type="number" step="0.01" min="0" value={payEditForm.amount} onChange={(e) => setPayEditForm(prev => ({ ...prev, amount: parseFloat(e.target.value || 0) }))} className="rounded-none mt-1" data-testid="fire-pay-edit-amount" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{t('source') || 'Source'}</Label>
                <select
                  value={payEditForm.payment_source}
                  onChange={(e) => setPayEditForm(prev => ({ ...prev, payment_source: e.target.value }))}
                  className="rounded-none mt-1 border border-zinc-200 bg-white text-sm px-3 py-2 w-full"
                  data-testid="fire-pay-edit-source"
                >
                  <option value="cash">{t('cash') || 'Cash'}</option>
                  <option value="bank">{t('bank') || 'Bank'}</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">{t('receiptNumber') || 'Receipt #'}</Label>
                <Input value={payEditForm.payment_number} onChange={(e) => setPayEditForm(prev => ({ ...prev, payment_number: e.target.value }))} className="rounded-none mt-1 font-mono" data-testid="fire-pay-edit-num" />
              </div>
            </div>
            <div>
              <Label className="text-xs">{t('notes') || 'Notes'}</Label>
              <Textarea value={payEditForm.notes} onChange={(e) => setPayEditForm(prev => ({ ...prev, notes: e.target.value }))} rows={3} className="rounded-none mt-1 resize-y" style={{ minHeight: '80px', maxHeight: '40vh' }} data-testid="fire-pay-edit-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayEditOpen(false)} className="rounded-none">{t('cancel') || 'Cancel'}</Button>
            <Button onClick={savePayEdit} className="rounded-none bg-zinc-900 hover:bg-zinc-800" data-testid="fire-pay-edit-save">
              <FloppyDisk size={14} className="mr-1" /> {t('save') || 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 356 — Reliable confirm-delete dialog (Shadcn Dialog) replaces
          the broken window.confirm() which was silently dismissed in some
          environments. */}
      <Dialog open={!!confirmDel} onOpenChange={(open) => { if (!open) closeConfirmDel(); }}>
        <DialogContent className="sm:max-w-md" data-testid="fire-confirm-delete-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
              {confirmDel?.kind === 'invoice'
                ? (t('deleteInvoice') || 'Delete invoice')
                : (t('deletePayment') || 'Delete payment')}
            </DialogTitle>
            <DialogDescription>
              {confirmDel?.kind === 'invoice'
                ? (t('confirmDeleteInvoiceLong') || 'This invoice will be removed from the Statement, Balance and any related PDFs. This action cannot be undone.')
                : (t('confirmDeletePaymentLong') || 'This payment will be reversed (cash / bank balance refunded) and removed from the Statement. This action cannot be undone.')}
            </DialogDescription>
          </DialogHeader>
          {confirmDel?.item && (
            <div className="border border-zinc-200 bg-zinc-50 p-3 text-xs space-y-1">
              {confirmDel.kind === 'invoice' ? (
                <>
                  <div><span className="text-zinc-500">{t('date') || 'Date'}:</span> <span className="font-mono">{formatDateDMY(confirmDel.item.date)}</span></div>
                  <div><span className="text-zinc-500">{t('amount') || 'Amount'}:</span> <span className="font-mono font-semibold">{fmtEur(confirmDel.item.amount)}</span></div>
                  {confirmDel.item.reference_number && <div><span className="text-zinc-500">{t('reference') || 'Reference'}:</span> {confirmDel.item.reference_number}</div>}
                  {confirmDel.item.notes && <div className="truncate"><span className="text-zinc-500">{t('notes') || 'Notes'}:</span> {confirmDel.item.notes}</div>}
                </>
              ) : (
                <>
                  <div><span className="text-zinc-500">{t('date') || 'Date'}:</span> <span className="font-mono">{formatDateDMY(confirmDel.item.date)}</span></div>
                  <div><span className="text-zinc-500">{t('amount') || 'Amount'}:</span> <span className="font-mono font-semibold">{fmtEur(confirmDel.item.amount)}</span></div>
                  <div><span className="text-zinc-500">{t('paymentSource') || 'Source'}:</span> {confirmDel.item.payment_source || confirmDel.item.source || '—'}</div>
                  {confirmDel.item.payment_number && <div><span className="text-zinc-500">{t('reference') || 'Reference'}:</span> {confirmDel.item.payment_number}</div>}
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeConfirmDel}
              className="rounded-none"
              disabled={confirmDel?.busy}
              data-testid="fire-confirm-delete-cancel"
            >
              {t('cancel') || 'Cancel'}
            </Button>
            <Button
              onClick={performConfirmedDelete}
              className="rounded-none bg-red-600 hover:bg-red-700 text-white"
              disabled={confirmDel?.busy}
              data-testid="fire-confirm-delete-confirm"
            >
              <Trash size={14} className="mr-1" />
              {confirmDel?.busy ? (t('deleting') || 'Deleting…') : (t('delete') || 'Delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 356 — Cert-record confirm-delete dialog. */}
      <Dialog open={!!certConfirmDel} onOpenChange={(open) => { if (!open) setCertConfirmDel(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="fire-cert-confirm-delete-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
              {t('deleteCertification') || 'Delete certification'}
            </DialogTitle>
            <DialogDescription>
              {t('confirmDeleteCertificationLong') || 'This certification record will be removed along with its history and related PDFs. Linked invoices and payments are preserved. This action cannot be undone.'}
            </DialogDescription>
          </DialogHeader>
          {certConfirmDel?.cert && (
            <div className="border border-zinc-200 bg-zinc-50 p-3 text-xs space-y-1">
              <div><span className="text-zinc-500">{t('company') || 'Company'}:</span> {certConfirmDel.cert.company_name || '—'}</div>
              <div><span className="text-zinc-500">{t('certificationDate') || 'Certification Date'}:</span> <span className="font-mono">{formatDateDMY(certConfirmDel.cert.certification_date)}</span></div>
              <div><span className="text-zinc-500">{t('totalAmount') || 'Total Amount'}:</span> <span className="font-mono font-semibold">{fmtEur(certConfirmDel.cert.total_amount)}</span></div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCertConfirmDel(null)}
              className="rounded-none"
              disabled={certConfirmDel?.busy}
              data-testid="fire-cert-confirm-delete-cancel"
            >
              {t('cancel') || 'Cancel'}
            </Button>
            <Button
              onClick={performCertDelete}
              className="rounded-none bg-red-600 hover:bg-red-700 text-white"
              disabled={certConfirmDel?.busy}
              data-testid="fire-cert-confirm-delete-confirm"
            >
              <Trash size={14} className="mr-1" />
              {certConfirmDel?.busy ? (t('deleting') || 'Deleting…') : (t('delete') || 'Delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="fire-cert-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
              {editingId ? (t('editCertification') || 'Edit Certification') : (t('addCertification') || 'Add Certification')}
            </DialogTitle>
            <DialogDescription>
              {t('fireCertHint') || 'Record the latest annual fire extinguisher inspection and maintenance contract details.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            {/* Company dropdown — only source of company assignment.
                Manual typing removed (Phase 342); manage the catalogue from
                Buildings → Fire Extinguisher Companies. */}
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs">{t('assignedCompany') || 'Assigned Company'} *</Label>
                <a
                  href="/fire-extinguisher-companies"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[10px] underline text-zinc-500 hover:text-zinc-900"
                  data-testid="fire-manage-companies-link"
                >
                  {t('manageCompanies') || 'Manage companies'} ↗
                </a>
              </div>
              <select
                value={form.company_id}
                onChange={(e) => {
                  const co = companies.find(c => c.company_id === e.target.value);
                  setForm(prev => ({ ...prev, company_id: e.target.value, company_name: co?.name || '' }));
                }}
                className="rounded-none mt-1 border border-zinc-200 bg-white text-sm px-3 py-2 w-full"
                data-testid="fire-cert-company-select"
              >
                <option value="">{t('selectCompany') || 'Select company…'}</option>
                {companies.map(c => (
                  <option key={c.company_id} value={c.company_id}>{c.name}</option>
                ))}
              </select>
              {companies.length === 0 && (
                <p className="text-[10px] text-orange-600 mt-1">
                  {t('noFireCompaniesHint') || 'No companies yet. Click "Manage companies" to add one first.'}
                </p>
              )}
              {/* Show the cert's stored company name when it no longer matches
                  any active row (e.g. the company was deleted) so the user
                  doesn't lose context. Read-only. */}
              {form.company_id && form.company_name && !companies.find(c => c.company_id === form.company_id) && (
                <p className="text-[10px] text-zinc-500 mt-1 italic">
                  {t('storedCompanyName') || 'Stored name'}: {form.company_name}
                </p>
              )}
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">{t('quantity') || 'Quantity'}</Label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={form.quantity}
                  onChange={(e) => { setTotalManuallyEdited(false); setForm(prev => ({ ...prev, quantity: parseInt(e.target.value || 0, 10) })); }}
                  className="rounded-none mt-1"
                  data-testid="fire-cert-quantity"
                />
              </div>
              <div>
                <Label className="text-xs">{t('unitPrice') || 'Unit Price'} (€)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.unit_price}
                  onChange={(e) => { setTotalManuallyEdited(false); setForm(prev => ({ ...prev, unit_price: parseFloat(e.target.value || 0) })); }}
                  className="rounded-none mt-1"
                  data-testid="fire-cert-unit-price"
                />
              </div>
              <div>
                <Label className="text-xs">{t('totalAmount') || 'Total'} (€)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.total_amount}
                  onChange={(e) => { setTotalManuallyEdited(true); setForm(prev => ({ ...prev, total_amount: parseFloat(e.target.value || 0) })); }}
                  className="rounded-none mt-1 font-semibold"
                  data-testid="fire-cert-total"
                />
                <p className="text-[10px] text-zinc-400 mt-0.5">{t('totalAutoHint') || 'Auto = qty × unit price. Editable.'}</p>
              </div>
            </div>

            <div>
              <Label className="text-xs">{t('contractNumber') || 'Contract Number'}</Label>
              <Input
                value={form.contract_number}
                onChange={(e) => setForm(prev => ({ ...prev, contract_number: e.target.value }))}
                className="rounded-none mt-1 font-mono"
                placeholder="FX-2026-001"
                data-testid="fire-cert-contract-number"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{t('certificationDate') || 'Certification Date'}</Label>
                <Input
                  type="date"
                  value={form.certification_date}
                  onChange={(e) => setForm(prev => ({ ...prev, certification_date: e.target.value }))}
                  className="rounded-none mt-1"
                  data-testid="fire-cert-certification-date"
                />
              </div>
              <div>
                <Label className="text-xs">{t('certificationExpiryDate') || 'Certification Expiry'}</Label>
                <Input
                  type="date"
                  value={form.certification_expiry_date}
                  onChange={(e) => setForm(prev => ({ ...prev, certification_expiry_date: e.target.value }))}
                  className="rounded-none mt-1"
                  data-testid="fire-cert-certification-expiry"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{t('contractStartDate') || 'Contract Start'}</Label>
                <Input
                  type="date"
                  value={form.contract_start_date}
                  onChange={(e) => setForm(prev => ({ ...prev, contract_start_date: e.target.value }))}
                  className="rounded-none mt-1"
                  data-testid="fire-cert-contract-start"
                />
              </div>
              <div>
                <Label className="text-xs">{t('contractEndDate') || 'Contract Expiry'}</Label>
                <Input
                  type="date"
                  value={form.contract_end_date}
                  onChange={(e) => setForm(prev => ({ ...prev, contract_end_date: e.target.value }))}
                  className="rounded-none mt-1"
                  data-testid="fire-cert-contract-end"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs">{t('notes') || 'Notes'}</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                rows={6}
                className="rounded-none mt-1 resize-y"
                style={{ minHeight: '140px', maxHeight: '60vh' }}
                placeholder={t('notesPh') || 'Multi-line notes…'}
                data-testid="fire-cert-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none" data-testid="fire-cert-cancel">
              {t('cancel') || 'Cancel'}
            </Button>
            <Button onClick={handleSave} className="rounded-none bg-zinc-900 hover:bg-zinc-800" data-testid="fire-cert-save">
              <FloppyDisk size={14} className="mr-1" /> {t('save') || 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
